import { useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";
import { PlayerChrome } from "@/components/pdp/PlayerChrome";
import { Sun, Moon } from "lucide-react";

/**
 * V3.1 "How to Use" as a full-width editorial ritual — sticky video on
 * the left, scrolling numbered steps on the right (Complete-Your-Routine
 * pattern the client already likes). Active step highlights as it hits
 * viewport center via IntersectionObserver, no scroll math.
 *
 * Mobile: video collapses on top (aspect 4/5), steps stack below, no
 * sticky and no active highlight — just a clean vertical read.
 *
 * Video source: `P.howToVideo` (mp4 url) if provided; otherwise falls back
 * to a still poster image from the gallery — client just drops in a URL
 * later, no other change needed.
 */
export const HowToRitualV31 = () => {
  const P = useProduct();
  const isV32 = !!P?.v32;
  const V32_STEPS = [
    "After cleansing, use 1 small pump of eye cream.",
    "Dot the eye cream along the orbital bone, outer corner and under-eye areas. Avoid applying directly to the eyelids to help prevent potential irritation or clogged oil glands.",
    "Gently pat in. Wait 2 minutes for the eye cream to sink in and fully absorb before applying makeup to the area.",
  ];
  const paragraph = P?.howToParagraph || null;
  const steps = paragraph ? [paragraph] : isV32 ? V32_STEPS : P?.details?.howTo || [];

  const videoSrc = P?.howToVideo || null;
  const posterImg =
    P?.howToPoster ||
    P?.beforeAfter?.after ||
    P?.gallery?.[2]?.src ||
    P?.gallery?.[0]?.src;

  const [activeStep, setActiveStep] = useState(0);
  const stepsRefs = useRef([]);

  // Active-step tracking. Root margin pushes the intersection line to the
  // vertical center of the viewport so a step "activates" the moment its
  // number sits in the middle of the screen — feels natural, no math.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.stepIndex);
            if (!Number.isNaN(idx)) setActiveStep(idx);
          }
        });
      },
      {
        // Zero-height detection band ~ vertical center.
        rootMargin: "-45% 0px -45% 0px",
        threshold: 0,
      }
    );
    stepsRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [steps.length]);

  if (!steps.length) return null;

  return (
    <section
      className="bg-white py-24 lg:py-32"
      data-testid="how-to-ritual-v31"
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT — heading + intro + numbered steps (copy) */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-[#0F2E2E]/60 mb-6">
                The Ritual
              </div>
              <h2 className="text-[2.5rem] md:text-[3rem] lg:text-[3.5rem] leading-[1.05] tracking-tight font-semibold text-neutral-900">
                How to use
              </h2>
              <p className="hidden text-[15px] md:text-base leading-relaxed text-neutral-500 mt-6 max-w-md">
                {isV32
                  ? "These steps can be combined and reduced — six steps makes it sound more complicated than it is."
                  : "Six quiet steps. A minute a day. That's all it takes for Eye Repair Treatment to do its work."}
              </p>
            </Reveal>

            {paragraph ? (
              <div className="mt-12 lg:mt-16" data-testid="ritual-paragraph">
                {(Array.isArray(paragraph) ? paragraph : [paragraph]).map((t, i) => (
                  <p key={i} className="text-[17px] md:text-[19px] leading-[1.6] text-neutral-800 max-w-[58ch] mt-5 first:mt-0" data-testid={`ritual-paragraph-${i + 1}`}>
                    {t}
                  </p>
                ))}
                {P?.whenToUseBlock && (
                  <div className="mt-12 pt-8 border-t border-neutral-200" data-testid="ritual-when-to-use">
                    <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-[#0F2E2E]/60 mb-4">
                      {P.whenToUseBlock.label}
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-[17px] md:text-[19px] font-medium text-neutral-900 mr-1">{P.whenToUseBlock.frequency}</span>
                      {P.whenToUseBlock.slots.map((s) => (
                        <span
                          key={s.label}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0F2E2E]/25 text-[#0F2E2E] font-mono text-[11px] tracking-[0.2em] uppercase"
                          data-testid={`ritual-slot-${s.label.toLowerCase()}`}
                        >
                          {s.icon === "sun" ? <Sun size={13} strokeWidth={1.8} /> : <Moon size={13} strokeWidth={1.8} />}
                          {s.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
            <ol className="mt-14 lg:mt-20 divide-y divide-neutral-200">
              {steps.map((text, i) => {
                const isActive = activeStep === i;
                return (
                  <li
                    key={i}
                    ref={(el) => (stepsRefs.current[i] = el)}
                    data-step-index={i}
                    data-active={isActive}
                    data-testid={`ritual-step-${i + 1}`}
                    className={`grid grid-cols-[auto_1fr] gap-6 md:gap-10 ${
                      isV32 ? "py-5 md:py-6 first:pt-2" : "py-10 md:py-14 first:pt-4"
                    } transition-opacity duration-500 ${
                      isV32 ? "opacity-100" : isActive ? "lg:opacity-100" : "lg:opacity-45"
                    }`}
                  >
                    <span
                      className={`${isV32 ? "text-[2.25rem] md:text-[2.75rem]" : "font-serif text-[3.5rem] md:text-[4.5rem]"} leading-[0.9] font-light tabular-nums select-none block transition-colors duration-500 ${
                        isV32 ? "text-[#0F2E2E]" : isActive ? "text-[#0F2E2E]" : "text-[#0F2E2E]/70"
                      }`}
                      style={isV32 ? { fontFamily: "'IBM Plex Mono', ui-monospace, monospace" } : undefined}
                      aria-hidden="true"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="pt-2 md:pt-4">
                      <p
                        className={`text-[16px] md:text-[17px] leading-[1.5] max-w-[54ch] transition-colors duration-500 ${
                          isV32 ? "text-neutral-800" : isActive ? "text-neutral-800" : "text-neutral-600"
                        }`}
                      >
                        {text}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
            )}

            {isV32 && !paragraph && (
              <p
                className="mt-8 text-[13px] md:text-sm text-neutral-700 border-l-2 border-[#0F2E2E]/40 pl-4 max-w-md"
                data-testid="ritual-footnote"
              >
                Use within 12 months.
              </p>
            )}

            {!paragraph && (
              <Reveal>
                <p className="mt-14 font-mono text-[11px] tracking-[0.24em] uppercase text-[#0F2E2E]/50">
                  A minute. Twice a day. Every day.
                </p>
              </Reveal>
            )}
          </div>

          {/* RIGHT — sticky square media with faux player chrome */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="lg:sticky lg:top-24">
              <div
                className="relative editorial-frame bg-gradient-to-b from-neutral-100 to-riversol-sand aspect-square overflow-hidden"
                data-testid="how-to-ritual-media"
                style={{ clipPath: `inset(0 round ${P?.howToMediaRadius || "15px"})` }}
              >
                {videoSrc ? (
                  <video
                    className="w-full h-full object-cover"
                    src={videoSrc}
                    poster={posterImg}
                    autoPlay
                    muted
                    loop
                    playsInline
                    aria-hidden="true"
                  />
                ) : (
                  posterImg && (
                    <img
                      src={posterImg}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )
                )}
                <PlayerChrome time="0:00 / 0:12" progress={0} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToRitualV31;
